import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import {
  ArrowRight,
  Check,
  Clock,
  Globe,
  Lock,
  Activity,
  TrendingDown,
  Zap,
  Search,
  Users,
} from 'lucide-react'

export const Route = createFileRoute('/_site/guides/monitor-saas-landing-page')({
  component: MonitorSaasLandingPageGuide,
  head: () => ({
    meta: [
      {
        title:
          'How to Monitor Your SaaS Landing Page Uptime — Uptrack',
      },
      {
        name: 'description',
        content:
          'Landing page downtime costs signups and revenue. Learn what to monitor (HTTP status, load time, SSL) and how to set up uptime monitoring with Uptrack.',
      },
      {
        property: 'og:title',
        content: 'How to Monitor Your SaaS Landing Page Uptime — Uptrack',
      },
      {
        property: 'og:description',
        content:
          'Your landing page is your best salesperson. If it goes down, you lose signups silently. Here\'s how to monitor it with 30-second checks.',
      },
      {
        property: 'og:image',
        content: 'https://uptrack.app/og/guides-monitor-saas-landing-page.png',
      },
      { property: 'og:type', content: 'article' },
      {
        property: 'og:url',
        content: 'https://uptrack.app/guides/monitor-saas-landing-page',
      },
    ],
    links: [
      {
        rel: 'canonical',
        href: 'https://uptrack.app/guides/monitor-saas-landing-page',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: 'How to Monitor Your SaaS Landing Page Uptime',
          description:
            'Landing page downtime costs signups and revenue. Learn what to monitor and how to set up uptime monitoring with Uptrack.',
          datePublished: '2026-05-22',
          dateModified: '2026-05-22',
          author: { '@type': 'Organization', name: 'Uptrack' },
          publisher: {
            '@type': 'Organization',
            name: 'Uptrack',
            url: 'https://uptrack.app',
          },
          mainEntityOfPage:
            'https://uptrack.app/guides/monitor-saas-landing-page',
        }),
      },
    ],
  }),
})

const competitors = [
  {
    name: 'Uptrack',
    freeMonitors: '5 monitors',
    minInterval: '30s (every plan)',
    sslMonitoring: 'Yes (paid plans)',
    falseAlerts: 'Consecutive checks',
    price: 'Free for 5, $3/mo for 50',
    highlight: true,
  },
  {
    name: 'UptimeRobot',
    freeMonitors: '50 monitors',
    minInterval: '5 min (free)',
    sslMonitoring: 'Paid only',
    falseAlerts: 'Single check',
    price: 'From $7/mo',
    highlight: false,
  },
  {
    name: 'Better Stack',
    freeMonitors: '5 monitors',
    minInterval: '3 min (free)',
    sslMonitoring: 'Yes',
    falseAlerts: 'Confirmation period',
    price: 'From $20/mo',
    highlight: false,
  },
  {
    name: 'Pingdom',
    freeMonitors: 'No free tier',
    minInterval: '1 min',
    sslMonitoring: 'Yes',
    falseAlerts: 'Single check',
    price: 'From $15/mo',
    highlight: false,
  },
]

function MonitorSaasLandingPageGuide() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <h1 className="font-sans mb-4 text-4xl font-semibold">
            How to Monitor Your SaaS Landing Page Uptime
          </h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            Your landing page is your best salesperson. It works 24/7, never
            takes a break, and handles every visitor at once. But when it goes
            down, there is no "Sorry, we're closed" sign. Visitors hit an
            error, bounce, and never come back. You don't even know it
            happened until someone tweets about it.
          </p>

          {/* Why it matters */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What landing page downtime actually costs you
            </h2>
            <p className="mb-4 text-lg leading-[1.7] text-muted-foreground">
              Downtime isn't just an inconvenience. For a SaaS company, every
              minute your landing page is unreachable translates directly to
              lost revenue. If your page converts at 3% and you get 200
              visitors per hour, one hour of downtime means 6 signups gone.
              At $50/mo per customer, that's $3,600 in annual recurring
              revenue from a single hour.
            </p>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              But the real cost is harder to measure. Paid ads keep running
              during downtime. Your Google Ads, Facebook campaigns, and
              newsletter links all point to a broken page. You're paying for
              traffic that bounces instantly. And those visitors won't click
              again.
            </p>
            <div className="grid gap-4 sm:grid-cols-3">
              <div className="rounded-lg border border-border bg-card p-5">
                <TrendingDown className="mb-3 h-6 w-6 text-status-down" />
                <h3 className="font-sans mb-2 font-semibold">Lost signups</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Visitors who hit an error page don't bookmark your URL and
                  try again later. They go to a competitor. That signup is
                  gone permanently.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Users className="mb-3 h-6 w-6 text-status-degraded" />
                <h3 className="font-sans mb-2 font-semibold">Wasted ad spend</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Your CPC doesn't pause when your site is down. Every click
                  during an outage is money burned with zero chance of
                  conversion.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Search className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">SEO damage</h3>
                <p className="text-lg leading-[1.7] text-body">
                  If Googlebot hits your page during downtime, it records a
                  crawl error. Repeated errors can push your page out of
                  search results entirely.
                </p>
              </div>
            </div>
          </section>

          {/* What to monitor */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              What to monitor on your landing page
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              "Is the page up?" is the minimum. Real monitoring goes deeper.
              Your landing page can return a 200 status code while showing a
              blank white page, a broken layout, or an expired SSL certificate
              warning. Here's what actually matters.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-5">
                <Activity className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">HTTP status code</h3>
                <p className="text-lg leading-[1.7] text-body">
                  The baseline. Your page should return 200. A 301 redirect
                  might mean your DNS is misconfigured. A 500 means your
                  server is crashing. A 403 means your CDN is blocking
                  traffic. Each tells a different story.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Clock className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Page load time</h3>
                <p className="text-lg leading-[1.7] text-body">
                  A page that takes 8 seconds to load is functionally down
                  for most visitors. Monitor response time and set a threshold.
                  If your landing page regularly exceeds 3 seconds, you have a
                  performance problem that's costing conversions.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Lock className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">SSL certificate</h3>
                <p className="text-lg leading-[1.7] text-body">
                  An expired SSL certificate shows a full-screen browser
                  warning that scares visitors away. Monitor certificate
                  expiry and get alerted days before it expires, not after
                  visitors start seeing "Your connection is not private."
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <Globe className="mb-3 h-6 w-6 text-primary" />
                <h3 className="font-sans mb-2 font-semibold">Response body content</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Check that your page actually contains expected content. A
                  200 response with "Error: application failed to start" in
                  the body is not a healthy page. Assert that your headline or
                  CTA text appears in the response.
                </p>
              </div>
            </div>
          </section>

          {/* Setup guide */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Set up landing page monitoring in 3 minutes
            </h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                  1
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Create an Uptrack account
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Sign up at uptrack.app. Start free with 5 monitors, or choose a plan for more. Pro gives you 50 monitors immediately with no
                    restrictions on monitoring capabilities.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                  2
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Add your landing page URL
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Click "Create Monitor" and paste your URL. Uptrack
                    automatically detects if it's HTTP or HTTPS. Set the
                    expected status code to 200. Add a response body assertion
                    for your headline text to catch blank page errors.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">
                  3
                </div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">
                    Choose your check interval and alerts
                  </h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Select how often to check. Every plan checks at
                    30-second intervals, and Pro includes 50 monitors for
                    $3/mo, so you can watch every critical landing page. Configure email or Slack alerts so you know the
                    moment something breaks.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Competitor comparison */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Landing page monitoring tools compared
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              If you're evaluating monitoring tools for your SaaS landing page,
              here's how the major options compare on the features that matter
              most.
            </p>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">
                      Provider
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Free monitors
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      Min interval
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      SSL monitoring
                    </th>
                    <th className="pb-3 px-3 text-center font-medium text-muted-foreground">
                      False alert prevention
                    </th>
                    <th className="pb-3 pl-3 text-center font-medium text-muted-foreground">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {competitors.map((c) => (
                    <tr
                      key={c.name}
                      className={`border-b last:border-0 ${c.highlight ? 'bg-muted' : ''}`}
                    >
                      <td
                        className={`py-3 pr-4 font-medium ${c.highlight ? 'text-primary' : ''}`}
                      >
                        {c.name}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {c.freeMonitors}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {c.minInterval}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {c.sslMonitoring}
                      </td>
                      <td className="py-3 px-3 text-center text-muted-foreground">
                        {c.falseAlerts}
                      </td>
                      <td
                        className={`py-3 pl-3 text-center ${c.highlight ? 'font-medium text-primary' : 'text-muted-foreground'}`}
                      >
                        {c.price}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Common mistakes */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">
              Common landing page monitoring mistakes
            </h2>
            <div className="space-y-4">
              {[
                {
                  title: 'Only monitoring the homepage',
                  description:
                    'Your pricing page, signup page, and demo booking page are all landing pages too. If your /pricing goes down while your homepage is fine, you lose every visitor at the decision stage. Monitor each critical page separately.',
                },
                {
                  title: 'Ignoring response time',
                  description:
                    'A page that loads in 12 seconds is technically "up" but effectively useless. Set a response time threshold. Google considers anything over 2.5 seconds as poor for Core Web Vitals, and slow pages tank your ad Quality Score.',
                },
                {
                  title: 'Not checking SSL expiry',
                  description:
                    'Let\'s Encrypt certificates expire every 90 days. Auto-renewal usually works, until it doesn\'t. A single expired cert shows a scary browser warning that kills all trust instantly. Monitor expiry proactively.',
                },
                {
                  title: 'Using 5-minute check intervals',
                  description:
                    'With a 5-minute interval, your page could be down for 4 minutes and 59 seconds before you even know. For landing pages receiving paid traffic, that gap is expensive. With 30-second checks on every plan, Uptrack detects issues 10x faster.',
                },
              ].map((mistake) => (
                <div key={mistake.title} className="flex gap-3">
                  <Check className="mt-1 h-4 w-4 shrink-0 text-primary" />
                  <div>
                    <span className="font-semibold">{mistake.title}</span>
                    <span className="text-muted-foreground">
                      {' '}
                      -- {mistake.description}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <Zap className="mx-auto mb-4 h-8 w-8 text-primary" />
            <h2 className="font-sans mb-3 text-xl font-semibold">
              Protect Your Landing Page Today
            </h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Pro includes 50 monitors at 30-second checks.
              Start free, or choose a plan for more.
            </p>
            <Button size="lg" asChild>
              <Link to="/pricing">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
